import { EventMarker, EventWithRels } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { atomWithDebounce } from '@/atoms/debounce';
import { filtersAtom } from '@/atoms/filters/filter';
import { pageAndOrderParamsAtom } from '@/atoms/general';
import { isMappedMetricPendingAtom, isMetricMappedAtom } from '@/atoms/metric/metricFilterMapping';
import { teamIdAtom } from '@/atoms/team/team';
import { selectedGameIdForVideosAtom } from '@/atoms/video';
import { EVENT_MARKERS_LIMIT } from '@/consts/visualisations';
import { splitPerspective, teamMetricEventsUrl, visEventsUrl } from '@/query/url';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { convertFilterParamsToString, gameEventParams, hasEmptyInFilters } from '@/utils/api';
import {
  convertEventDataIntoDescriptions,
  convertMarkerDataIntoMarkers,
  convertMarkerDataIntoDescriptions,
  convertMarkerDataToShotMapMarkers,
  processEventDataAtom,
} from '@/utils/atoms/eventData';
import { createUnwrappedPitchEventsAtom } from '@/utils/atoms/vis';
import { fetchClientAtom } from '../client';

export const teamEventMetricFiltersDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
export const { debouncedValueAtom: teamEventMetricFiltersAtom } = teamEventMetricFiltersDebounceObject;

const teamRawEventMarkersAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'eventMarkersForMetric',
    get(filtersAtom).metric,
    get(teamIdAtom),
    get(teamEventMetricFiltersAtom),
    get(isMetricMappedAtom),
    get(isMappedMetricPendingAtom),
  ] as const;

  const queryFn = async ({
    queryKey: [, metricKey, teamId, filterParams, isMetricMapped, isPending],
  }: {
    queryKey: typeof queryKey;
  }) => {
    if (isPending || !teamId || !metricKey || hasEmptyInFilters(filterParams)) return [];
    const url = visEventsUrl(
      'team',
      teamId,
      metricKey,
      'event-markers',
      { ...filterParams, limit: EVENT_MARKERS_LIMIT },
      isMetricMapped,
    );
    if (!url) return [];
    return (await fetch(url)) as Promise<EventMarker[]>;
  };

  return { queryKey, queryFn };
});

const teamEventMarkersDataAtom = atom(async get => {
  const rawMarkerData = await get(teamRawEventMarkersAtom);
  return {
    markers: convertMarkerDataIntoMarkers(rawMarkerData.data),
    descriptions: convertMarkerDataIntoDescriptions(rawMarkerData.data),
  };
});

export const teamEventMarkersForMetricAtom = atom(async get => {
  const { markers } = await get(teamEventMarkersDataAtom);
  return markers;
});

export const unwrappedTeamEventMarkersForMetricAtom = createUnwrappedPitchEventsAtom(teamEventMarkersForMetricAtom);

const teamRawEventsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'eventsForMetric',
    get(filtersAtom).metric,
    get(teamIdAtom),
    get(teamEventMetricFiltersAtom),
    get(pageAndOrderParamsAtom),
    get(isMetricMappedAtom),
    get(isMappedMetricPendingAtom),
  ] as const;

  const queryFn = async ({
    queryKey: [, metricKey, teamId, filterParams, pageAndOrderParams, isMetricMapped, isPending],
  }: {
    queryKey: typeof queryKey;
  }) => {
    if (isPending || !teamId || !metricKey || hasEmptyInFilters(filterParams)) return [];

    const url = visEventsUrl(
      'team',
      teamId,
      metricKey,
      'events',
      { ...pageAndOrderParams, ...filterParams },
      isMetricMapped,
    );
    if (!url) return [];
    return (await fetch(url)) as Promise<EventWithRels[]>;
  };

  return { queryKey, queryFn };
});
export const teamEventsForMetricAtom = atom(async get => processEventDataAtom(await get(teamRawEventsAtom)));

const teamRawEventsCountAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'eventsForMetricCount',
    get(filtersAtom).metric,
    get(teamIdAtom),
    get(teamEventMetricFiltersAtom),
    get(isMetricMappedAtom),
    get(isMappedMetricPendingAtom),
  ] as const;

  const queryFn = async ({
    queryKey: [, metricKey, teamId, filterParams, isMetricMapped, isPending],
  }: {
    queryKey: typeof queryKey;
  }) => {
    if (isPending || !teamId || !metricKey || hasEmptyInFilters(filterParams)) return 0;
    const url = visEventsUrl('team', teamId, metricKey, 'events/count', { ...filterParams }, isMetricMapped);
    if (!url) return 0;
    return (await fetch(url)) as Promise<number>;
  };

  return { queryKey, queryFn };
});
export const teamEventsForMetricCountAtom = atom(async get => (await get(teamRawEventsCountAtom)).data);

const teamRawEventsForMetricInGameAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'teamEventsForMetricInGame',
    get(filtersAtom).metric,
    get(teamIdAtom),
    get(selectedGameIdForVideosAtom),
  ] as const;

  const queryFn = async ({ queryKey: [, metricKey, teamId, gameId] }: { queryKey: typeof queryKey }) => {
    const url = teamMetricEventsUrl(teamId, metricKey, gameEventParams(gameId));
    if (!url || !gameId) return [];

    return (await fetch(url)) as Promise<EventWithRels[]>;
  };

  return { queryKey, queryFn };
});

export const teamEventMarkersDescriptionsAtom = atom(async get => {
  const { descriptions } = await get(teamEventMarkersDataAtom);
  return descriptions;
});

export const teamGameEventDescriptionsAtom = atom(async get => {
  const teamRawEventsForMetricInGame = await get(teamRawEventsForMetricInGameAtom);
  return convertEventDataIntoDescriptions(teamRawEventsForMetricInGame.data);
});

export const teamEventDataDescriptionsAtom = atom(async get => {
  const teamRawEvents = await get(teamRawEventsAtom);
  return convertEventDataIntoDescriptions(teamRawEvents.data);
});

const rawTeamShotMarkersAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);

  const queryKey = ['teamShotMarkers', get(teamIdAtom), get(teamEventMetricFiltersAtom)] as const;

  const queryFn = async ({ queryKey: [, teamId, filterParams] }: { queryKey: typeof queryKey }) => {
    if (!teamId || hasEmptyInFilters(filterParams)) return [];
    // the shot map does not follow the Events For/Against toggle: keep the perspective out of its URL
    const { params } = splitPerspective({ ...filterParams });
    return (await fetch(
      `/event-markers${convertFilterParamsToString({
        ...params,
        limit: EVENT_MARKERS_LIMIT,
      })}&eq[event.team_id]=${teamId}&in[event.type]=shot␞own_goal_for&neq[event.period]=5`,
    )) as Promise<EventMarker[]>;
  };

  return { queryKey, queryFn };
});

export const teamShotMarkersAtom = atom(async get => {
  const teamShotMarkers = await get(rawTeamShotMarkersAtom);
  return convertMarkerDataToShotMapMarkers(teamShotMarkers.data);
});

export const unwrappedTeamShotMarkersAtom = unwrap(teamShotMarkersAtom, prev => prev || []);
